import { useCallback, useEffect, useState } from 'react'
import * as db from '../lib/db'
import { formatMoney } from '../lib/billing'
import { todayKey } from '../lib/time'
import { ROLE_LABEL, type ID, type Role } from '../lib/types'

/**
 * The two pieces the Team screen grew when sign-up moved to
 * request-and-approve and charge-out rates arrived. Kept in their own file so
 * TeamScreen stays readable, and so both can be dropped in without
 * restructuring the member card around them.
 */

/* ------------------------------------------------------------------ */
/* Pending access requests                                             */
/* ------------------------------------------------------------------ */

export function useAccessRequests(workspaceId: ID, enabled: boolean) {
  const [requests, setRequests] = useState<db.AccessRequest[]>([])
  const [loading, setLoading] = useState(enabled)

  const reload = useCallback(async () => {
    if (!enabled) return
    try {
      setRequests(await db.listAccessRequests(workspaceId))
    } catch {
      // A member has no business seeing the queue; an empty list is correct.
      setRequests([])
    } finally {
      setLoading(false)
    }
  }, [workspaceId, enabled])

  useEffect(() => {
    void reload()
  }, [reload])

  return { requests, loading, reload }
}

export function AccessRequestsPanel({
  requests,
  onDecide,
  busy,
  acceptsSignups,
}: {
  requests: db.AccessRequest[]
  onDecide: (id: ID, approve: boolean, role: Role) => void
  busy: boolean
  /** Whether self-signups reach this queue at all. */
  acceptsSignups: boolean
}) {
  const [roles, setRoles] = useState<Record<string, Role>>({})

  /*
   * The panel used to disappear when the queue was empty, which meant an
   * empty queue and a queue nothing can ever reach looked exactly the same:
   * a blank space. An admin waiting for approvals to show up had no way to
   * tell "nobody has applied" from "sign-ups are switched off", and the
   * second one is silent by nature -- there is no error to notice.
   *
   * So it is always on screen for whoever can act on it, and it says which
   * of the two situations this is.
   */
  if (requests.length === 0) {
    return (
      <div className="card card--pad">
        <div className="card-title">Waiting for approval</div>
        {acceptsSignups ? (
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '6px 0 0' }}>
            Nobody is waiting. When somebody signs themselves up, they appear here for you to
            approve or turn down before they can see any of the workspace.
          </p>
        ) : (
          <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '6px 0 0' }}>
            This workspace is not accepting sign-ups, so nobody can reach this queue — people can
            only join by being invited. Turn on{' '}
            <strong style={{ color: 'var(--ink)' }}>Allow people to request access</strong> in
            Settings if you want them to be able to apply.
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="card card--pad">
      <div className="card-title">
        Waiting for approval
        <span
          style={{
            marginLeft: 8,
            fontSize: 12,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 20,
            background: 'var(--ac-soft)',
            border: '1px solid var(--ac-line)',
            color: '#8C3A1C',
          }}
        >
          {requests.length}
        </span>
      </div>
      <p className="pretty" style={{ fontSize: 12.5, color: 'var(--mut)', margin: '4px 0 14px' }}>
        These people created an account but cannot see anything until you let them in. Pick a role
        and approve, or reject the request.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {requests.map((r) => {
          const role = roles[r.id] ?? 'member'
          return (
            <div
              key={r.id}
              className="card"
              style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
            >
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div className="trunc" style={{ fontWeight: 600, fontSize: 14 }}>
                  {r.fullName}
                </div>
                <div className="trunc" style={{ fontSize: 12.5, color: 'var(--mut)' }}>
                  {r.email}
                </div>
              </div>

              <select
                className="role-select"
                value={role}
                aria-label={`Role for ${r.email}`}
                onChange={(e) => setRoles((prev) => ({ ...prev, [r.id]: e.target.value as Role }))}
              >
                <option value="member">{ROLE_LABEL.member}</option>
                <option value="manager">{ROLE_LABEL.manager}</option>
                <option value="admin">{ROLE_LABEL.admin}</option>
              </select>

              <button
                type="button"
                className="btn btn--primary"
                disabled={busy}
                onClick={() => onDecide(r.id, true, role)}
              >
                Approve
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => onDecide(r.id, false, role)}
              >
                Reject
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** The currencies rates may be set in. */
/* USD first because it is the default; the rest follow it. */
export const CURRENCIES = ['USD', 'SGD', 'EUR', 'GBP', 'JPY'] as const

/* ------------------------------------------------------------------ */
/* Hourly rates                                                        */
/* ------------------------------------------------------------------ */

export function useRates(workspaceId: ID) {
  const [rates, setRates] = useState<db.HourlyRate[]>([])

  const reload = useCallback(async () => {
    try {
      setRates(await db.listRates(workspaceId))
    } catch {
      // Members only ever see their own; a failure means none to show.
      setRates([])
    }
  }, [workspaceId])

  useEffect(() => {
    void reload()
  }, [reload])

  return { rates, reload }
}

/** The rate in force today for one person, or null if they have none yet. */
export function currentRate(rates: db.HourlyRate[], userId: ID): db.HourlyRate | null {
  const today = todayKey()
  let best: db.HourlyRate | null = null
  for (const r of rates) {
    if (r.userId !== userId) continue
    if (r.effectiveFrom > today) continue
    if (!best || r.effectiveFrom > best.effectiveFrom) best = r
  }
  return best
}

export function RateRow({
  userId,
  userName,
  rates,
  canEdit,
  onSave,
  busy,
}: {
  userId: ID
  userName: string
  rates: db.HourlyRate[]
  canEdit: boolean
  onSave: (userId: ID, amount: number, effectiveFrom: string, currency: string) => void
  busy: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [amount, setAmount] = useState('')
  const [from, setFrom] = useState(todayKey())
  const [currency, setCurrency] = useState<string>('USD')
  const [problem, setProblem] = useState('')

  const current = currentRate(rates, userId)
  const history = rates
    .filter((r) => r.userId === userId)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))

  const save = () => {
    const value = Number(String(amount).replace(/[^\d.]/g, ''))
    if (!Number.isFinite(value) || value < 0) {
      setProblem('Enter an amount of 0 or more.')
      return
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      setProblem('Pick a date.')
      return
    }
    setProblem('')
    setEditing(false)
    onSave(userId, value, from, currency)
  }

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
      <div className="spread" style={{ fontSize: 12, color: 'var(--mut)' }}>
        <span>Charge-out rate</span>
        <span className="mono" style={{ color: current ? 'var(--ink)' : 'var(--dg)' }}>
          {current ? `${formatMoney(current.hourlyRate, current.currency)} / h` : 'No rate set'}
        </span>
      </div>

      {canEdit && !editing && (
        <div className="row" style={{ gap: 12, marginTop: 8 }}>
          {/* A real button, not a line of text. This is the only way to price
              somebody, and as a muted link it was being missed entirely. */}
          <button
            type="button"
            className="btn btn--sm btn--accent"
            onClick={() => {
              setAmount(current ? String(current.hourlyRate) : '')
              setFrom(todayKey())
              // Default to whatever this person is already billed in, so a
              // change of rate does not quietly become a change of currency.
              setCurrency(current?.currency ?? 'USD')
              setEditing(true)
            }}
          >
            Set new rate
          </button>
          {history.length > 0 && (
            <button
              type="button"
              className="linkbtn"
              style={{ color: 'var(--mut)' }}
              onClick={() => setShowHistory((v) => !v)}
            >
              {showHistory ? 'Hide history' : `History (${history.length})`}
            </button>
          )}
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 8 }}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <input
              className="input"
              style={{ width: 110 }}
              inputMode="decimal"
              placeholder="150.00"
              aria-label={`New hourly rate for ${userName}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <select
              className="role-select"
              aria-label={`Currency for ${userName}`}
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              className="input"
              style={{ width: 150 }}
              type="date"
              aria-label={`Effective from, for ${userName}`}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <button type="button" className="btn btn--primary" disabled={busy} onClick={save}>
              Save
            </button>
            <button type="button" className="btn" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          <p style={{ fontSize: 11.5, color: 'var(--mut)', margin: '6px 0 0' }}>
            Past entries keep the rate that was in force on the day they were logged.
          </p>
          {problem && (
            <p style={{ fontSize: 12, color: 'var(--dg)', margin: '4px 0 0' }}>{problem}</p>
          )}
        </div>
      )}

      {showHistory && history.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--mut)' }}>
          {history.map((r) => (
            <div key={r.id} className="spread" style={{ padding: '2px 0' }}>
              <span>from {r.effectiveFrom}</span>
              <span className="mono">{formatMoney(r.hourlyRate, r.currency)} / h</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
